import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ReservationFilters.module.scss';
import { useToasts } from 'react-toast-notifications';
import { DATE_FORMAT_DEFAUT, LOGGER_LOG_TYPE, removeAccents } from 'Config';
import Logger from 'common/services/Logger';
import { FaCalendarAlt } from 'react-icons/fa';
import Button from 'common/components/button/Button';
import { Col, Row } from 'react-flexbox-grid';
import FormItem from 'common/components/formItem/FormItem';
import Label from 'common/components/label/Label';
import SelectController from 'common/components/select/SelectController';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import { useForm } from 'react-hook-form';
import InputGroup from 'common/components/inputGroup/InputGroup';
import DateWithHourController from 'common/components/dateWithHour/DateWithHourController';
import DriversService from 'api/drivers/DriversService';
import InputController from 'common/components/input/InputController';

export interface Filters {
    reservationStartDate?: Date | null,
    reservationEndDate?: Date | null,
    availabilityStartDate?: Date | null,
    availabilityEndDate?: Date | null,
    driverId?: string | null,
    driverName?: string | null,
    registrationNumber?: string | null,
    page?: number,
    orderBy?: string,
    orderColumn?: string,
    status?: string | null,
    requestDateStart?: Date | null,
    requestDateEnd?: Date | null,
}

type Props = {
    filters: Filters;
    onFilter: (filters: Filters) => void;
    load?: boolean;
    mode?: 'reservations' | 'requests';
}

const ReservationFilters: React.FC<Props> = ({ filters, onFilter, load, mode = 'requests' }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const form = useForm<Filters>({ shouldUnregister: false, shouldFocusError: true, defaultValues: filters });
    const { getValues, setValue, reset } = form;
    const [driversOptions, setDriversOptions] = useState<SelectValueLabel[]>([]);

    // Status options for requests
    const statusOptions: SelectValueLabel[] = [
        { value: 'PENDING_APPROVAL', label: t('reservations.filters.status_pending') as string },
        { value: 'APPROVED', label: t('reservations.filters.status_approved') as string },
        { value: 'REJECTED', label: t('reservations.filters.status_rejected') as string },
    ];

    useEffect(() => {
        if (load) {
            form.reset(filters);
            onSubmit(filters);
        } else {
            form.reset(filters);
        }
    }, [filters]);

    const getData = async () => {
        try {
            const drivers = await DriversService.catalog();
            setDriversOptions(drivers);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.VEHICLES, `Couldn't get data`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        }
    }

    useEffect(() => {
        getData();
    }, []);

    const clearFilters = () => {
        const model: Filters = mode === 'reservations'
            ? {
                availabilityStartDate: null,
                availabilityEndDate: null,
                reservationStartDate: null,
                reservationEndDate: null,
                driverId: null,
                driverName: null,
                registrationNumber: null,
            }
            : {
                reservationStartDate: null,
                reservationEndDate: null,
                requestDateStart: null,
                requestDateEnd: null,
                driverId: null,
                driverName: null,
                registrationNumber: null,
                status: null,
            };
        reset(model);
        onSubmit(model);
        getData();
    }

    const onSubmit = (f: Filters) => {
        onFilter(f);
    }

    const renderReservationsFilters = () => (
        <>
            <div className={styles.sectionTitle}>{t('reservations.filters.search_availability')}</div>
            <Row>
                <Col sm={12} md={6} xl={6}>
                    <FormItem>
                        <Label className={styles.filtersLabel}>{t('reservations.filters.date_start')}</Label>
                        <DateWithHourController
                            form={form}
                            datePlaceholder={t('common.date')}
                            dateFormat={DATE_FORMAT_DEFAUT}
                            dateCustomInput={<InputGroup icon={<FaCalendarAlt />} removeIconOrTextBorderRadius={true} />}
                            name="availabilityStartDate"
                            isDisabled={false}
                            timePlaceholder={t('common.hour')}
                        />
                    </FormItem>
                </Col>
                <Col sm={12} md={6} xl={6}>
                    <FormItem>
                        <Label className={styles.filtersLabel}>{t('reservations.filters.date_end')}</Label>
                        <DateWithHourController
                            form={form}
                            datePlaceholder={t('common.date')}
                            dateFormat={DATE_FORMAT_DEFAUT}
                            dateCustomInput={<InputGroup icon={<FaCalendarAlt />} removeIconOrTextBorderRadius={true} />}
                            name="availabilityEndDate"
                            isDisabled={false}
                            timePlaceholder={t('common.hour')}
                            minDate={form.watch('availabilityStartDate') || undefined}
                        />
                    </FormItem>
                </Col>
            </Row>

            <div className={styles.sectionTitle}>{t('reservations.filters.search_reservations')}</div>
            <Row>
                <Col sm={12} md={6} xl={6}>
                    <FormItem>
                        <Label className={styles.filtersLabel}>{t('reservations.filters.date_start')}</Label>
                        <DateWithHourController
                            form={form}
                            datePlaceholder={t('common.date')}
                            dateFormat={DATE_FORMAT_DEFAUT}
                            dateCustomInput={<InputGroup icon={<FaCalendarAlt />} removeIconOrTextBorderRadius={true} />}
                            name="reservationStartDate"
                            isDisabled={false}
                            timePlaceholder={t('common.hour')}
                        />
                    </FormItem>
                </Col>
                <Col sm={12} md={6} xl={6}>
                    <FormItem>
                        <Label className={styles.filtersLabel}>{t('reservations.filters.date_end')}</Label>
                        <DateWithHourController
                            form={form}
                            datePlaceholder={t('common.date')}
                            dateFormat={DATE_FORMAT_DEFAUT}
                            dateCustomInput={<InputGroup icon={<FaCalendarAlt />} removeIconOrTextBorderRadius={true} />}
                            name="reservationEndDate"
                            isDisabled={false}
                            timePlaceholder={t('common.hour')}
                            minDate={form.watch('reservationStartDate') || undefined}
                        />
                    </FormItem>
                </Col>
                <Col sm={12} md={6} xl={6}>
                    <FormItem>
                        <Label className={styles.filtersLabel}>{t('reservations.filters.driver')}</Label>
                        <SelectController
                            form={form}
                            name="driverId"
                            placeholder={t('reservations.filters.driver')}
                            options={driversOptions}
                            isDisabled={false}
                            isClearable={true}
                            filterOption={(candidate: any, input: any) => input ? removeAccents(candidate.label).toUpperCase().includes(removeAccents(input).toUpperCase()) : true}
                            onChangeSelect={(data: SelectValueLabel) => {
                                setValue('driverId', data ? data.value : null);
                                setValue('driverName', data ? data.label : null);
                                getValues();
                            }}
                        />
                    </FormItem>
                </Col>
                <Col sm={12} md={6} xl={6}>
                    <FormItem>
                        <Label className={styles.filtersLabel}>{t('reservations.filters.registration')}</Label>
                        <InputController
                            form={form as any}
                            name="registrationNumber"
                            placeholder={t('reservations.filters.registration')}
                        />
                    </FormItem>
                </Col>
            </Row>
        </>
    );

    const renderRequestsFilters = () => (
        <Row>
            <Col sm={12} md={6} xl={6}>
                <FormItem>
                    <Label className={styles.filtersLabel}>{t('reservations.filters.date_start')}</Label>
                    <DateWithHourController
                        form={form}
                        datePlaceholder={t('common.date')}
                        dateFormat={DATE_FORMAT_DEFAUT}
                        dateCustomInput={<InputGroup icon={<FaCalendarAlt />} removeIconOrTextBorderRadius={true} />}
                        name="reservationStartDate"
                        isDisabled={false}
                        timePlaceholder={t('common.hour')}
                    />
                </FormItem>
            </Col>

            <Col sm={12} md={6} xl={6}>
                <FormItem>
                    <Label className={styles.filtersLabel}>{t('reservations.filters.date_end')}</Label>
                    <DateWithHourController
                        form={form}
                        datePlaceholder={t('common.date')}
                        dateFormat={DATE_FORMAT_DEFAUT}
                        dateCustomInput={<InputGroup icon={<FaCalendarAlt />} removeIconOrTextBorderRadius={true} />}
                        name="reservationEndDate"
                        isDisabled={false}
                        timePlaceholder={t('common.hour')}
                        minDate={form.watch('reservationStartDate') || undefined}
                    />
                </FormItem>
            </Col>

            <Col sm={12} md={6} xl={6}>
                <FormItem>
                    <Label className={styles.filtersLabel}>{t('reservations.filters.request_date_start')}</Label>
                    <DateWithHourController
                        form={form}
                        datePlaceholder={t('common.date')}
                        dateFormat={DATE_FORMAT_DEFAUT}
                        dateCustomInput={<InputGroup icon={<FaCalendarAlt />} removeIconOrTextBorderRadius={true} />}
                        name="requestDateStart"
                        isDisabled={false}
                        timePlaceholder={t('common.hour')}
                    />
                </FormItem>
            </Col>

            <Col sm={12} md={6} xl={6}>
                <FormItem>
                    <Label className={styles.filtersLabel}>{t('reservations.filters.request_date_end')}</Label>
                    <DateWithHourController
                        form={form}
                        datePlaceholder={t('common.date')}
                        dateFormat={DATE_FORMAT_DEFAUT}
                        dateCustomInput={<InputGroup icon={<FaCalendarAlt />} removeIconOrTextBorderRadius={true} />}
                        name="requestDateEnd"
                        isDisabled={false}
                        timePlaceholder={t('common.hour')}
                        minDate={form.watch('requestDateStart') || undefined}
                    />
                </FormItem>
            </Col>

            <Col sm={12} md={6} xl={6}>
                <FormItem>
                    <Label className={styles.filtersLabel}>{t('reservations.filters.driver')}</Label>
                    <SelectController
                        form={form}
                        name="driverId"
                        placeholder={t('reservations.filters.driver')}
                        options={driversOptions}
                        isDisabled={false}
                        isClearable={true}
                        filterOption={(candidate: any, input: any) => input ? removeAccents(candidate.label).toUpperCase().includes(removeAccents(input).toUpperCase()) : true}
                        onChangeSelect={(data: SelectValueLabel) => {
                            setValue('driverId', data ? data.value : null);
                            setValue('driverName', data ? data.label : null);
                            getValues();
                        }}
                    />
                </FormItem>
            </Col>

            <Col sm={12} md={6} xl={6}>
                <FormItem>
                    <Label className={styles.filtersLabel}>{t('reservations.filters.registration')}</Label>
                    <InputController
                        form={form as any}
                        name="registrationNumber"
                        placeholder={t('reservations.filters.registration')}
                    />
                </FormItem>
            </Col>

            <Col sm={12} md={6} xl={6}>
                <FormItem>
                    <Label className={styles.filtersLabel}>{t('reservations.filters.status')}</Label>
                    <SelectController
                        form={form}
                        name="status"
                        placeholder={t('reservations.filters.status_all')}
                        options={statusOptions}
                        isDisabled={false}
                        isClearable={true}
                        filterOption={(candidate: any, input: any) => input ? removeAccents(candidate.label).toUpperCase().includes(removeAccents(input).toUpperCase()) : true}
                        onChangeSelect={(data: SelectValueLabel) => {
                            setValue('status', data ? data.value : null);
                            getValues();
                        }}
                    />
                </FormItem>
            </Col>
        </Row>
    );

    return (
        <div className={styles.formContent}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
            {mode === 'reservations' ? renderReservationsFilters() : renderRequestsFilters()}

            <Row>
                <Col xl={12} className={styles.searchButton}>
                    <Button
                        text={t('common.clear')}
                        size={'small'}
                        preset={'secondary'}
                        onClick={clearFilters}
                        type='reset'
                    />
                    <Button
                        type='submit'
                        text={t('common.apply')}
                        size={'small'}
                    />
                </Col>
            </Row>
        </form>
        </div>
    );
}

export default memo(ReservationFilters);
